/**
 * Color Code Store
 * 
 * Manages runtime color assignments for the Color Code Data feature.
 * 
 * Each palette slot tracks a `lastAssigned` timestamp:
 * - 0 means never assigned (highest priority to be taken)
 * - Otherwise, the epoch ms when it was last given to a center
 * 
 * When a new center needs a color, we sort available slots by lastAssigned
 * ascending and take the oldest one. This ensures never-used slots go first,
 * then the least-recently-assigned ones.
 * 
 * A slot is "available" if it's not currently held by a center that's still
 * in the viewport. Off-screen centers keep their assignment as long as no
 * new center claims the slot.
 */

import { create } from 'zustand';
import { type ColorCodeConfig } from '../../../types/metadata';

export interface ColorAssignment {
  value: string;           // The join field value (e.g., a center_id)
  paletteIndex: number;    // Index into the palette
  color: [number, number, number]; // Resolved RGB color
}

interface PaletteSlot {
  lastAssigned: number;    // 0 = never assigned, otherwise epoch ms
  assignedTo: string | null; // join field value currently holding this slot
}

interface PointWithCoords {
  joinValue: string;
  lng: number;
  lat: number;
}

interface ColorCodeState {
  /**
   * Maps: layerId -> { joinFieldValue -> ColorAssignment }
   * Active color assignments for rendering.
   */
  assignments: Record<string, Record<string, ColorAssignment>>;

  /**
   * Palette slot tracking: layerId -> PaletteSlot[]
   * One entry per palette color, tracks when it was last assigned.
   */
  slots: Record<string, PaletteSlot[]>;

  /**
   * Compute color assignments based on viewport-visible centers.
   */
  computeAssignments: (
    layerId: string,
    visiblePoints: PointWithCoords[],
    viewportCenter: [number, number],
    config: ColorCodeConfig
  ) => void;

  /**
   * Get the color override for a specific point based on its join field value.
   */
  getColorForValue: (layerId: string, value: string | undefined) => [number, number, number] | null;

  /**
   * Clear all assignments for a layer.
   */
  clearAssignments: (layerId: string) => void;

  /**
   * Clear everything.
   */
  clearAll: () => void;
}

function distanceSquared(lng1: number, lat1: number, lng2: number, lat2: number): number {
  const dLng = lng2 - lng1;
  const dLat = lat2 - lat1;
  return dLng * dLng + dLat * dLat;
}

export const useColorCodeStore = create<ColorCodeState>((set, get) => ({
  assignments: {},
  slots: {},

  computeAssignments: (layerId, visiblePoints, viewportCenter, config) => {
    const { slots: prevSlots, assignments: prevAssignments } = get();
    const palette = config.palette;
    const paletteSize = palette.length;

    if (paletteSize === 0) {
      set((state) => ({
        assignments: { ...state.assignments, [layerId]: {} },
      }));
      return;
    }

    // Initialize or resize slots for this layer
    let layerSlots: PaletteSlot[] = prevSlots[layerId]
      ? [...prevSlots[layerId]]
      : [];
    
    // Ensure slots array matches palette size
    while (layerSlots.length < paletteSize) {
      layerSlots.push({ lastAssigned: 0, assignedTo: null });
    }
    // Trim if palette shrunk
    if (layerSlots.length > paletteSize) {
      layerSlots = layerSlots.slice(0, paletteSize);
    }

    // Get unique visible values with their closest point to center
    const visibleMap = new Map<string, { lng: number; lat: number; dist: number }>();
    for (const pt of visiblePoints) {
      const dist = distanceSquared(pt.lng, pt.lat, viewportCenter[0], viewportCenter[1]);
      const existing = visibleMap.get(pt.joinValue);
      if (!existing || dist < existing.dist) {
        visibleMap.set(pt.joinValue, { lng: pt.lng, lat: pt.lat, dist });
      }
    }

    const visibleValuesSet = new Set(Array.from(visibleMap.keys()));
    const now = Date.now();

    // Step 1: Identify which slots are held by centers still in viewport (locked)
    // vs slots whose owner left the viewport (reclaimable)
    const lockedSlots = new Set<number>(); // indices that can't be taken
    const valueToSlot = new Map<string, number>(); // current value -> slot index

    for (let i = 0; i < paletteSize; i++) {
      const slot = layerSlots[i];
      if (slot.assignedTo !== null) {
        valueToSlot.set(slot.assignedTo, i);
        if (visibleValuesSet.has(slot.assignedTo)) {
          lockedSlots.add(i); // Owner still visible — can't reclaim
        }
      }
    }

    // Step 2: Find visible values that already have a slot (keep them)
    // and new values that need a slot
    const alreadyAssigned: string[] = [];
    const needsSlot: string[] = [];

    const visibleValuesArray = Array.from(visibleValuesSet);
    for (const val of visibleValuesArray) {
      if (valueToSlot.has(val)) {
        alreadyAssigned.push(val);
        lockedSlots.add(valueToSlot.get(val)!); // Lock their slot
      } else {
        needsSlot.push(val);
      }
    }

    // Sort values needing a slot by distance to viewport center (closest first)
    needsSlot.sort((a, b) => {
      const distA = visibleMap.get(a)!.dist;
      const distB = visibleMap.get(b)!.dist;
      return distA - distB;
    });

    // Step 3: Get reclaimable slots sorted by lastAssigned ascending (oldest first)
    // A slot is reclaimable if it's not locked (owner is off-screen or never assigned)
    const reclaimable: number[] = [];
    for (let i = 0; i < paletteSize; i++) {
      if (!lockedSlots.has(i)) {
        reclaimable.push(i);
      }
    }
    reclaimable.sort((a, b) => layerSlots[a].lastAssigned - layerSlots[b].lastAssigned);

    // Step 4: Assign slots to new values
    for (const val of needsSlot) {
      if (reclaimable.length === 0) break;
      const slotIdx = reclaimable.shift()!;
      layerSlots[slotIdx] = { lastAssigned: now, assignedTo: val };
      lockedSlots.add(slotIdx);
    }

    // Step 5: Build the final assignments map
    const newAssignments: Record<string, ColorAssignment> = {};
    for (let i = 0; i < paletteSize; i++) {
      const slot = layerSlots[i];
      if (slot.assignedTo !== null) {
        newAssignments[slot.assignedTo] = {
          value: slot.assignedTo,
          paletteIndex: i,
          color: palette[i] as [number, number, number],
        };
      }
    }

    set((state) => ({
      assignments: { ...state.assignments, [layerId]: newAssignments },
      slots: { ...state.slots, [layerId]: layerSlots },
    }));
  },

  getColorForValue: (layerId, value) => {
    if (value === undefined || value === null) return null;
    const layerAssignments = get().assignments[layerId];
    if (!layerAssignments) return null;
    const assignment = layerAssignments[String(value)];
    return assignment ? assignment.color : null;
  },

  clearAssignments: (layerId) => {
    set((state) => {
      const { [layerId]: _, ...restAssignments } = state.assignments;
      const { [layerId]: __, ...restSlots } = state.slots;
      return { assignments: restAssignments, slots: restSlots };
    });
  },

  clearAll: () => {
    set({ assignments: {}, slots: {} });
  },
}));
